/* Integrations — Dedicated card-grid page for all external API connections.
   Replaces the old Settings → API Bağlantıları tab. Includes everything:
   - Bol Retailer + Advertiser (2 ayrı API)
   - ShipsGo + webhook secret
   - Billit + Party ID
   - Cargoboard + sandbox mode
   - Quicargo
   - Telegram + notification toggles
   - Default Pickup Address (for transport providers)
*/
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { safeCopy } from '../utils/clipboard';
import {
    Plug, Loader2, Save, CheckCircle2, XCircle, ShieldCheck, Trash2,
    Send, Bell, Webhook, Search as SearchIcon, MapPin, FlaskConical,
    Copy, ShoppingBag, Ship, Truck, FileText, Megaphone, Printer, AlertCircle, ExternalLink, Package,
} from 'lucide-react';
import {
    usePrintNodeConfig,
    usePrintNodePrinters,
    useSavePrintNodeConfig,
    useDeletePrintNodeConfig,
} from '../hooks/usePrintNode';
import {
    useBpostConfig,
    useSaveBpostConfig,
    useDeleteBpostConfig,
    useBpostTestShipment,
} from '../hooks/useBpost';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ─── shared building blocks ─────────────────────────────────────────────── */

function ProviderCard({ name, desc, configured, icon: Icon, accent = 'blue', testId, children }) {
    const accentMap = {
        blue: 'text-blue-600 bg-blue-50',
        emerald: 'text-emerald-700 bg-emerald-50',
        amber: 'text-amber-700 bg-amber-50',
        purple: 'text-purple-700 bg-purple-50',
        cyan: 'text-cyan-700 bg-cyan-50',
        sky: 'text-sky-700 bg-sky-50',
        rose: 'text-rose-700 bg-rose-50',
    };
    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-sm transition-shadow"
            data-testid={testId || `integration-card-${name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentMap[accent]}`}>
                        {Icon ? <Icon size={16} /> : <Plug size={16} />}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 truncate">{name}</h3>
                </div>
                {configured ? (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 size={11} /> Configured
                    </span>
                ) : (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md bg-gray-50 text-gray-500 border border-gray-200">
                        <XCircle size={11} /> Not connected
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-500 mb-4">{desc}</p>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Field({ label, value, onChange, hasSaved, type = 'password', placeholder, testId }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
                placeholder={hasSaved ? '•••••• (saved — re-enter to update)' : (placeholder || '')}
                className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:border-blue-500 outline-none placeholder-gray-400"
                data-testid={testId || `field-${label.toLowerCase().replace(/\s+/g, '-')}`} />
        </div>
    );
}

function BtnRow({ children }) {
    return <div className="flex items-center gap-2 flex-wrap pt-1">{children}</div>;
}

function PrimaryBtn({ onClick, loading, disabled, icon: Icon, children, testId }) {
    return (
        <button onClick={onClick} disabled={loading || disabled}
            className="h-9 px-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-lg flex items-center gap-1.5"
            data-testid={testId}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : (Icon && <Icon size={13} />)}
            {children}
        </button>
    );
}

function GhostBtn({ onClick, loading, icon: Icon, children, testId, danger }) {
    return (
        <button onClick={onClick} disabled={loading}
            className={`h-9 px-3 text-sm font-semibold border rounded-lg flex items-center gap-1.5 disabled:opacity-40 ${
                danger
                    ? 'text-rose-700 bg-white border-rose-200 hover:bg-rose-50'
                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            }`}
            data-testid={testId}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : (Icon && <Icon size={13} />)}
            {children}
        </button>
    );
}

function Toggle({ checked, onChange, label, testId }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-700">
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 accent-blue-600" data-testid={testId} />
            <span>{label}</span>
        </label>
    );
}

/* ─── BOL.COM (Retailer + Advertiser) ─────────────────────────────────────── */

function BolCard({ refresh }) {
    const [cfg, setCfg] = useState({});
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [advClientId, setAdvClientId] = useState('');
    const [advClientSecret, setAdvClientSecret] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [subs, setSubs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [wstats, setWstats] = useState(null);

    const load = useCallback(() => {
        axios.get(`${API}/api/bol/config`, { headers: hdrs() })
            .then(r => setCfg(r.data || {}))
            .catch(() => {});
        axios.get(`${API}/api/bol/subscriptions`, { headers: hdrs() })
            .then(r => setSubs(r.data?.subscriptions || r.data?.items || []))
            .catch(() => {});
        axios.get(`${API}/api/bol/webhook/queue-stats`, { headers: hdrs() })
            .then(r => setWstats(r.data || null))
            .catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    // NOT: Backend client_secret'i güvenlik için response'tan çıkarıyor,
    // o yüzden sadece client_id'nin var olması yeterli (kaydedildi demek).
    const retailerConfigured = !!cfg.client_id;
    const advertiserConfigured = !!cfg.adv_client_id;

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            if (clientId) payload.client_id = clientId;
            if (clientSecret) payload.client_secret = clientSecret;
            if (advClientId) payload.adv_client_id = advClientId;
            if (advClientSecret) payload.adv_client_secret = advClientSecret;
            if (!Object.keys(payload).length) { toast.error('En az bir alan girin'); setSaving(false); return; }
            await axios.put(`${API}/api/bol/config`, payload, { headers: hdrs() });
            toast.success('Bol.com kaydedildi');
            setClientId(''); setClientSecret(''); setAdvClientId(''); setAdvClientSecret('');
            load(); refresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kayit hatasi');
        } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await axios.post(`${API}/api/bol/test`, {}, { headers: hdrs() });
            toast.success(r.data?.message || 'Bol.com baglantisi basarili');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Baglanti hatasi');
        } finally { setTesting(false); }
    };

    const subscribe = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/api/bol/subscribe`, {}, { headers: hdrs() });
            toast.success(`Webhook aktif: ${r.data?.events?.join(', ') || 'ok'}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Webhook hatasi');
        } finally { setBusy(false); }
    };

    const unsubscribe = async (subId) => {
        if (!window.confirm('Webhook aboneligi kaldirilsin mi?')) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/api/bol/subscriptions/${subId}`, { headers: hdrs() });
            toast.success('Abonelik kaldirildi');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setBusy(false); }
    };

    return (
        <ProviderCard name="Bol.com" desc="Retailer (siparis + komisyon) ve Advertiser (sponsorlu reklam) — 2 ayri API." configured={retailerConfigured} icon={ShoppingBag} accent="blue">
            <div>
                <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1.5">
                    Retailer API {retailerConfigured && <span className="text-emerald-600 ml-1">✓</span>}
                </div>
                <div className="space-y-2">
                    <Field label="Client ID" value={clientId} onChange={setClientId} hasSaved={!!cfg.client_id} testId="bol-client-id" />
                    <Field label="Client Secret" value={clientSecret} onChange={setClientSecret} hasSaved={!!cfg.client_secret} testId="bol-client-secret" />
                </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
                <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1.5">
                    Advertiser API {advertiserConfigured && <span className="text-emerald-600 ml-1">✓</span>}
                </div>
                <div className="space-y-2">
                    <Field label="Advertiser Client ID" value={advClientId} onChange={setAdvClientId} hasSaved={!!cfg.adv_client_id} testId="bol-adv-client-id" />
                    <Field label="Advertiser Client Secret" value={advClientSecret} onChange={setAdvClientSecret} hasSaved={!!cfg.adv_client_secret} testId="bol-adv-client-secret" />
                </div>
            </div>

            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="bol-save">Save</PrimaryBtn>
                <GhostBtn onClick={test} loading={testing} icon={ShieldCheck} testId="bol-test">Test connection</GhostBtn>
            </BtnRow>

            {/* Webhook subscription */}
            {retailerConfigured && (
                <div className="pt-3 border-t border-gray-100">
                    <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                        <Webhook size={11} /> Push Webhook
                    </div>
                    {subs.length === 0 ? (
                        <div className="space-y-1.5">
                            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                ⚠️ Bol webhook&apos;a abone degilsiniz — yeni siparis/kargo bildirimleri sadece 60dk&apos;da bir sync ile gelir. Anlik bildirim icin abone olun.
                            </div>
                            <GhostBtn onClick={subscribe} loading={busy} icon={Bell} testId="bol-subscribe">Webhook&apos;a Abone Ol</GhostBtn>
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {subs.map((s) => (
                                <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-emerald-900">✓ {(s.resources || []).join(', ')}</div>
                                        <div className="text-[10px] text-emerald-700 truncate font-mono">{s.url}</div>
                                    </div>
                                    <button onClick={() => unsubscribe(s.id)} disabled={busy}
                                        className="h-7 px-2 text-[11px] font-semibold text-rose-700 bg-white hover:bg-rose-50 border border-rose-200 rounded-md disabled:opacity-50"
                                        data-testid={`bol-unsubscribe-${s.id}`}>
                                        Kaldir
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {wstats && (
                        <div className="mt-2 grid grid-cols-4 gap-1.5 text-[10px]" data-testid="bol-webhook-stats">
                            <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-center">
                                <div className="font-black text-gray-900">{wstats.last_24h || 0}</div>
                                <div className="text-gray-500">24s</div>
                            </div>
                            <div className="px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-center">
                                <div className="font-black text-emerald-900">{wstats.stats?.processed || 0}</div>
                                <div className="text-emerald-600">OK</div>
                            </div>
                            <div className="px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-center">
                                <div className="font-black text-amber-900">{wstats.stats?.pending || 0}</div>
                                <div className="text-amber-600">Kuyrukta</div>
                            </div>
                            <div className="px-2 py-1.5 bg-rose-50 border border-rose-200 rounded text-center">
                                <div className="font-black text-rose-900">{wstats.stats?.failed || 0}</div>
                                <div className="text-rose-600">Hata</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ProviderCard>
    );
}

/* ─── SHIPSGO ─────────────────────────────────────────────────────────────── */

function ShipsGoCard({ refresh }) {
    const [cfg, setCfg] = useState({});
    const [token, setToken] = useState('');
    const [webhookSecret, setWebhookSecret] = useState('');
    const [saving, setSaving] = useState(false);
    const [savingSecret, setSavingSecret] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/api/logistics/config`, { headers: hdrs() })
            .then(r => setCfg(r.data || {}))
            .catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!token) { toast.error('Token girin'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/logistics/config`, { shipsgo_token: token }, { headers: hdrs() });
            toast.success('ShipsGo kaydedildi'); setToken(''); load(); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const saveSecret = async () => {
        if (!webhookSecret) { toast.error('Secret girin'); return; }
        setSavingSecret(true);
        try {
            await axios.post(`${API}/api/logistics/config`, { webhook_secret: webhookSecret }, { headers: hdrs() });
            toast.success('Webhook secret kaydedildi'); setWebhookSecret(''); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSavingSecret(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await axios.get(`${API}/api/logistics/test`, { headers: hdrs() });
            toast.success(r.data?.message || 'ShipsGo bağlantısı çalışıyor');
        } catch (e) { toast.error(e.response?.data?.detail || 'Baglanti hatasi'); }
        finally { setTesting(false); }
    };

    const disconnect = async () => {
        if (!window.confirm('ShipsGo baglantisi kaldirilsin mi?')) return;
        try {
            await axios.delete(`${API}/api/logistics/config`, { headers: hdrs() });
            toast.success('Kaldirildi'); load(); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/logistics/webhook/shipsgo`;
    const copyUrl = () => {
        safeCopy(webhookUrl);
        toast.success('Webhook URL kopyalandi');
    };

    return (
        <ProviderCard name="ShipsGo" desc="Container B/L tracking. Use your User Token from the ShipsGo dashboard." configured={!!cfg.shipsgo_configured} icon={Ship} accent="cyan">
            <Field label="User Token" value={token} onChange={setToken} hasSaved={!!cfg.shipsgo_configured} testId="shipsgo-token" />
            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="shipsgo-save">Save</PrimaryBtn>
                <GhostBtn onClick={test} loading={testing} icon={ShieldCheck} testId="shipsgo-test">Test connection</GhostBtn>
                {cfg.shipsgo_configured && <GhostBtn onClick={disconnect} icon={Trash2} testId="shipsgo-disconnect" danger>Kaldir</GhostBtn>}
            </BtnRow>

            <div className="pt-3 border-t border-gray-100">
                <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1">
                    <Webhook size={11} /> Webhook Ayarlari
                </div>
                <div className="space-y-2">
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">Webhook URL (ShipsGo dashboard&apos;una yapistir)</label>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 px-2 py-1.5 text-[11px] bg-gray-50 border border-gray-200 rounded text-gray-700 font-mono truncate">{webhookUrl}</code>
                            <button onClick={copyUrl} className="h-8 w-8 flex items-center justify-center bg-white border border-gray-200 hover:bg-gray-50 rounded">
                                <Copy size={12} />
                            </button>
                        </div>
                    </div>
                    <Field label={`Webhook Secret ${cfg.webhook_secret_set ? '(set)' : ''}`} value={webhookSecret} onChange={setWebhookSecret} hasSaved={!!cfg.webhook_secret_set} testId="shipsgo-webhook-secret" />
                    <GhostBtn onClick={saveSecret} loading={savingSecret} icon={Save} testId="shipsgo-save-secret">Secret Kaydet</GhostBtn>
                </div>
            </div>
        </ProviderCard>
    );
}

/* ─── BILLIT ──────────────────────────────────────────────────────────────── */

function BillitCard({ refresh }) {
    const [cfg, setCfg] = useState({});
    const [apiKey, setApiKey] = useState('');
    const [partyId, setPartyId] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/api/billit/config`, { headers: hdrs() })
            .then(r => {
                setCfg(r.data || {});
                if (r.data?.party_id) setPartyId(r.data.party_id);
            })
            .catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const configured = !!(cfg.api_key && cfg.enabled);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { enabled: true };
            if (apiKey) payload.api_key = apiKey; else if (cfg.api_key) payload.api_key = cfg.api_key;
            if (partyId) payload.party_id = partyId;
            await axios.put(`${API}/api/billit/config`, payload, { headers: hdrs() });
            toast.success('Billit kaydedildi'); setApiKey(''); load(); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await axios.post(`${API}/api/billit/test`, { api_key: apiKey || cfg.api_key || null, party_id: partyId || null }, { headers: hdrs() });
            toast.success(r.data?.message || 'Billit OK');
        } catch (e) { toast.error(e.response?.data?.detail || 'Baglanti hatasi'); }
        finally { setTesting(false); }
    };

    const disconnect = async () => {
        if (!window.confirm('Billit baglantisi kaldirilsin mi?')) return;
        try {
            await axios.delete(`${API}/api/billit/config`, { headers: hdrs() });
            toast.success('Kaldirildi'); setApiKey(''); setPartyId(''); load(); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    return (
        <ProviderCard name="Billit" desc="Belcika muhasebe — fatura push & musteri senkronizasyonu." configured={configured} icon={FileText} accent="purple">
            <Field label="API Key" value={apiKey} onChange={setApiKey} hasSaved={!!cfg.api_key} testId="billit-api-key" />
            <Field label="Party ID (multi-company hesaplar icin)" value={partyId} onChange={setPartyId} hasSaved={false} type="text" testId="billit-party-id" />
            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="billit-save">Save</PrimaryBtn>
                <GhostBtn onClick={test} loading={testing} icon={ShieldCheck} testId="billit-test">Test connection</GhostBtn>
                {configured && <GhostBtn onClick={disconnect} icon={Trash2} testId="billit-disconnect" danger>Kaldir</GhostBtn>}
            </BtnRow>
        </ProviderCard>
    );
}

/* ─── CARGOBOARD ──────────────────────────────────────────────────────────── */

function CargoboardCard({ cfg, refresh }) {
    const [key, setKey] = useState('');
    const [saving, setSaving] = useState(false);
    const hasSaved = !!cfg?.cargoboard_configured;

    const save = async () => {
        if (!key) { toast.error('API key girin'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/transport/config`, { cargoboard_api_key: key }, { headers: hdrs() });
            toast.success('Cargoboard kaydedildi'); setKey(''); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('Cargoboard kaldirilsin mi?')) return;
        try {
            await axios.delete(`${API}/api/transport/config/cargoboard`, { headers: hdrs() });
            toast.success('Kaldirildi'); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const toggleSandbox = async (val) => {
        try {
            await axios.post(`${API}/api/transport/config`, { cargoboard_sandbox: val }, { headers: hdrs() });
            refresh?.();
        } catch (e) { toast.error('Sandbox toggle hatasi'); }
    };

    return (
        <ProviderCard name="Cargoboard" desc="Karayolu nakliye siparisleri ve status sync." configured={hasSaved} icon={Truck} accent="amber">
            <Field label="API Key" value={key} onChange={setKey} hasSaved={hasSaved} testId="cargoboard-key" />
            <Toggle checked={cfg?.cargoboard_sandbox} onChange={toggleSandbox}
                label={<>Sandbox modu <FlaskConical size={11} className="inline ml-1 text-amber-700" /></>} testId="cargoboard-sandbox" />
            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="cargoboard-save">Save</PrimaryBtn>
                {hasSaved && <GhostBtn onClick={remove} icon={Trash2} testId="cargoboard-disconnect" danger>Kaldir</GhostBtn>}
            </BtnRow>
        </ProviderCard>
    );
}

/* ─── QUICCARGO ───────────────────────────────────────────────────────────── */

function QuicargoCard({ cfg, refresh }) {
    const [key, setKey] = useState('');
    const [saving, setSaving] = useState(false);
    const hasSaved = !!cfg?.quicargo_configured;

    const save = async () => {
        if (!key) { toast.error('API key girin'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/transport/config`, { quicargo_api_key: key }, { headers: hdrs() });
            toast.success('Quicargo kaydedildi'); setKey(''); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('Quicargo kaldirilsin mi?')) return;
        try {
            await axios.delete(`${API}/api/transport/config/quicargo`, { headers: hdrs() });
            toast.success('Kaldirildi'); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    return (
        <ProviderCard name="Quicargo" desc="Quicargo siparis senkronizasyonu." configured={hasSaved} icon={Truck} accent="amber">
            <Field label="API Key" value={key} onChange={setKey} hasSaved={hasSaved} testId="quicargo-key" />
            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="quicargo-save">Save</PrimaryBtn>
                {hasSaved && <GhostBtn onClick={remove} icon={Trash2} testId="quicargo-disconnect" danger>Kaldir</GhostBtn>}
            </BtnRow>
        </ProviderCard>
    );
}

/* ─── TELEGRAM ────────────────────────────────────────────────────────────── */

function TelegramCard({ refresh }) {
    const [cfg, setCfg] = useState({});
    const [botToken, setBotToken] = useState('');
    const [chatId, setChatId] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [findingChat, setFindingChat] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/api/telegram/config`, { headers: hdrs() })
            .then(r => {
                setCfg(r.data || {});
                if (r.data?.chat_id) setChatId(r.data.chat_id);
            })
            .catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const configured = !!(cfg.has_token && cfg.chat_id && cfg.enabled !== false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { enabled: true };
            if (botToken) payload.bot_token = botToken;
            if (chatId) payload.chat_id = chatId;
            if (typeof cfg.new_order !== 'undefined') payload.new_order = cfg.new_order;
            if (typeof cfg.shipped_report !== 'undefined') payload.shipped_report = cfg.shipped_report;
            if (typeof cfg.daily_report !== 'undefined') payload.daily_report = cfg.daily_report;
            if (typeof cfg.weekly_report !== 'undefined') payload.weekly_report = cfg.weekly_report;
            await axios.put(`${API}/api/telegram/config`, payload, { headers: hdrs() });
            toast.success('Telegram kaydedildi'); setBotToken(''); load(); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const findChatId = async () => {
        const tokenToUse = botToken.trim();
        if (!tokenToUse && !cfg.has_token) { toast.error('Önce Bot Token gir'); return; }
        setFindingChat(true);
        try {
            const params = tokenToUse ? { token: tokenToUse } : {};
            const r = await axios.get(`${API}/api/telegram/find-chat-id`, { headers: hdrs(), params });
            if (r.data?.chat_id) { setChatId(String(r.data.chat_id)); toast.success(`Chat ID bulundu: ${r.data.chat_id}`); }
            else toast.error('Chat ID bulunamadi — botla "/start" yazip tekrar deneyin');
        } catch (e) { toast.error(e.response?.data?.detail || 'Chat ID hatasi'); }
        finally { setFindingChat(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            await axios.post(`${API}/api/telegram/test`, {}, { headers: hdrs() });
            toast.success('Test mesaji gonderildi');
        } catch (e) { toast.error(e.response?.data?.detail || 'Test hatasi'); }
        finally { setTesting(false); }
    };

    const toggleNotif = async (k, v) => {
        const newCfg = { ...cfg, [k]: v };
        setCfg(newCfg);
        try {
            await axios.put(`${API}/api/telegram/config`, { [k]: v, enabled: true }, { headers: hdrs() });
        } catch { toast.error('Bildirim toggle hatasi'); setCfg(cfg); }
    };

    return (
        <ProviderCard name="Telegram" desc="Yeni siparis + gunluk/haftalik rapor bildirimleri." configured={configured} icon={Send} accent="sky">
            <Field label="Bot Token" value={botToken} onChange={setBotToken} hasSaved={!!cfg.has_token} testId="telegram-bot-token" />
            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Chat ID</label>
                <div className="flex items-center gap-2">
                    <input type="text" value={chatId} onChange={(e) => setChatId(e.target.value)}
                        placeholder="Botla /start yapip 'Bul' butonuna tikla"
                        className="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:border-blue-500 outline-none"
                        data-testid="telegram-chat-id" />
                    <GhostBtn onClick={findChatId} loading={findingChat} icon={SearchIcon} testId="telegram-find-chat">Bul</GhostBtn>
                </div>
            </div>

            {/* Notification toggles */}
            <div className="pt-2 border-t border-gray-100 space-y-1.5">
                <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Bell size={11} /> Bildirimler</div>
                <Toggle checked={cfg.new_order !== false} onChange={(v) => toggleNotif('new_order', v)} label="🛒 Yeni siparis (anlik, webhook)" testId="tg-toggle-new-order" />
                <Toggle checked={cfg.shipped_report !== false} onChange={(v) => toggleNotif('shipped_report', v)} label="📦 Kargolandi (anlik, webhook)" testId="tg-toggle-shipped" />
                <Toggle checked={cfg.daily_report !== false} onChange={(v) => toggleNotif('daily_report', v)} label="📊 Gunluk rapor (22:00 NL)" testId="tg-toggle-daily" />
                <Toggle checked={cfg.weekly_report !== false} onChange={(v) => toggleNotif('weekly_report', v)} label="📅 Haftalik rapor (Pazar 20:00 NL)" testId="tg-toggle-weekly" />
            </div>

            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="telegram-save">Save</PrimaryBtn>
                <GhostBtn onClick={test} loading={testing} icon={Megaphone} testId="telegram-test">Test mesaj</GhostBtn>
            </BtnRow>
        </ProviderCard>
    );
}

/* ─── DEFAULT PICKUP (Transport) ───────────────────────────────────────────── */

function PrintNodeCard() {
    const cfgQuery = usePrintNodeConfig();
    const cfg = cfgQuery.data;
    const configured = !!cfg?.configured;
    const printersQuery = usePrintNodePrinters({ enabled: configured });
    const saveMut = useSavePrintNodeConfig();
    const deleteMut = useDeletePrintNodeConfig();

    const [apiKey, setApiKey] = useState('');
    const [defaultPrinterId, setDefaultPrinterId] = useState(null);

    useEffect(() => {
        if (cfg?.default_printer_id) setDefaultPrinterId(cfg.default_printer_id);
    }, [cfg?.default_printer_id]);

    const handleSave = async () => {
        if (!apiKey) {
            toast.error('API key gir');
            return;
        }
        try {
            const printer = (printersQuery.data?.printers || []).find((p) => p.id === defaultPrinterId);
            await saveMut.mutateAsync({
                apiKey,
                defaultPrinterId,
                defaultPrinterName: printer?.name,
            });
            toast.success('PrintNode yapılandırıldı');
            setApiKey('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('PrintNode bağlantısını kaldır?')) return;
        try {
            await deleteMut.mutateAsync();
            toast.success('Bağlantı kaldırıldı');
        } catch {
            toast.error('Hata');
        }
    };

    return (
        <ProviderCard
            name="PrintNode"
            desc="Uzaktan yazdırma — fabrikadaki yazıcıya internetten etiket bas. Tatildeyken telefondan da çalışır."
            configured={configured}
            icon={Printer}
            accent="cyan"
            testId="integration-card-printnode">
            {!configured ? (
                <>
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-[11px] text-cyan-900 leading-relaxed">
                        <b>Kurulum:</b><br />
                        1. <a href="https://app.printnode.com" target="_blank" rel="noreferrer" className="underline font-semibold">app.printnode.com</a> → hesap aç<br />
                        2. <b>Account → API Keys → Create</b><br />
                        3. Fabrika PC&apos;sine <a href="https://www.printnode.com/download" target="_blank" rel="noreferrer" className="underline font-semibold">PrintNode Client</a> kur (5 dk)<br />
                        4. API key&apos;i buraya yapıştır
                    </div>
                    <Field label="PrintNode API Key" type="text" value={apiKey} onChange={setApiKey}
                        placeholder="u8gJWXV5yU..."
                        testId="printnode-api-key" />
                    <BtnRow>
                        <PrimaryBtn onClick={handleSave} loading={saveMut.isPending} icon={Save} testId="printnode-save">
                            Bağla
                        </PrimaryBtn>
                    </BtnRow>
                </>
            ) : (
                <>
                    <div className="bg-gray-50 rounded-md p-2 text-xs flex items-center justify-between">
                        <div>
                            <div className="text-gray-500 text-[10px]">API Key</div>
                            <div className="font-mono text-gray-900">{cfg.api_key_masked}</div>
                        </div>
                        <a href="https://app.printnode.com" target="_blank" rel="noreferrer"
                            className="text-[11px] text-cyan-700 hover:underline inline-flex items-center gap-1">
                            Dashboard <ExternalLink size={10} />
                        </a>
                    </div>

                    <div>
                        <div className="text-[11px] font-semibold text-gray-700 mb-1.5">Varsayılan Yazıcı</div>
                        {printersQuery.isLoading ? (
                            <div className="text-center py-3"><Loader2 className="animate-spin mx-auto text-gray-400" size={16} /></div>
                        ) : (printersQuery.data?.printers || []).length === 0 ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-[11px] text-amber-900">
                                <AlertCircle size={11} className="inline mr-1 -mt-0.5" />
                                Yazıcı yok. Fabrika PC&apos;sinde PrintNode Client açık mı?
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {(printersQuery.data?.printers || []).map((p) => (
                                    <label key={p.id}
                                        className={`flex items-center justify-between gap-2 p-2 rounded-md border cursor-pointer text-[11px] ${
                                            defaultPrinterId === p.id ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
                                        }`}>
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <input type="radio" name="pn-default"
                                                checked={defaultPrinterId === p.id}
                                                onChange={() => setDefaultPrinterId(p.id)} />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-gray-900 truncate">{p.name}</div>
                                                <div className="text-[10px] text-gray-500 truncate">
                                                    {p.computer_name && `${p.computer_name} · `}
                                                    <span className={p.state === 'online' ? 'text-emerald-700 font-semibold' : 'text-rose-700'}>
                                                        {p.state}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {defaultPrinterId !== cfg.default_printer_id && (
                        <>
                            <Field label="API Key (varsayılan yazıcı değişikliğini kaydetmek için tekrar gir)"
                                type="text" value={apiKey} onChange={setApiKey}
                                placeholder="PrintNode API key"
                                testId="printnode-api-key-rekey" />
                            <BtnRow>
                                <PrimaryBtn onClick={handleSave} loading={saveMut.isPending} icon={Save} testId="printnode-save-default">
                                    Varsayılanı Kaydet
                                </PrimaryBtn>
                            </BtnRow>
                        </>
                    )}

                    <BtnRow>
                        <GhostBtn onClick={handleDisconnect} loading={deleteMut.isPending} icon={Trash2} danger testId="printnode-disconnect">
                            Bağlantıyı Kaldır
                        </GhostBtn>
                    </BtnRow>
                </>
            )}
        </ProviderCard>
    );
}

function BpostSenderForm() {
    const testMut = useBpostTestShipment();
    const [result, setResult] = useState(null);

    const runTest = async () => {
        setResult(null);
        try {
            const data = await testMut.mutateAsync();
            setResult(data);
            if (data.ok) {
                toast.success('bpost akışı tam çalışıyor');
            } else if (data.error_code === 'bpost_default_sender_missing') {
                toast.error('Default Sender hâlâ eksik', { duration: 8000 });
            } else {
                toast.error('Test başarısız — detayları aşağıda gör', { duration: 8000 });
            }
        } catch (e) {
            const msg = e.response?.data?.detail || 'Test başarısız';
            setResult({ ok: false, error: msg });
            toast.error(msg, { duration: 8000 });
        }
    };

    return (
        <div className="rounded-md border border-rose-300 bg-rose-50/70 p-3 text-[11px] text-rose-950 leading-relaxed space-y-2">
            <div className="flex items-start gap-1.5">
                <AlertCircle size={13} className="mt-0.5 shrink-0 text-rose-700" />
                <div>
                    <b>1 kez yapılacak — bpost portal&apos;da &quot;Default Sender&quot; ayarı</b>
                    <div className="mt-0.5 text-rose-800">
                        bpost API, gönderici bilgisini her shipment&apos;ta payload&apos;dan değil, portaldaki
                        <b> Default Sender</b>&apos;dan okur. FirstName eksikse etiket basamayız.
                    </div>
                </div>
            </div>
            <ol className="ml-5 list-decimal space-y-0.5 text-rose-900">
                <li>
                    <a href="https://www.bpost.be/ShmBackEnd/" target="_blank" rel="noreferrer"
                        className="underline font-semibold">bpost Shipping Manager</a> — giriş yap
                </li>
                <li><b>Admin → Sender addresses</b> (sol/üst menü)</li>
                <li>
                    Mevcut adresin yanındaki <b>kırmızı kalem</b>&apos;e tıkla
                    {' '}(adres yoksa <b>Add address</b>)
                </li>
                <li>
                    Tüm zorunlu alanları doldur:
                    {' '}<code className="px-1 rounded bg-white/70 border border-rose-200">
                        First name · Last name · Street · N° · Postal code · City · Country
                    </code>
                </li>
                <li><b>Default address = Yes</b> işaretle</li>
                <li>Save / Save configuration</li>
            </ol>
            <div className="pt-1 border-t border-rose-200/70">
                <button
                    onClick={runTest}
                    disabled={testMut.isPending}
                    data-testid="bpost-test-shipment-btn"
                    className="text-[11px] font-semibold px-3 py-1.5 rounded-md bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-50 transition-colors">
                    {testMut.isPending ? 'Test ediliyor…' : 'Akışı Test Et (sipariş gerektirmez)'}
                </button>
                <span className="ml-2 text-rose-800/80">
                    Portal&apos;da kaydet, sonra burada test et.
                </span>
            </div>
            {result && (
                <div
                    data-testid="bpost-test-result"
                    className={`mt-1.5 rounded-md p-2 text-[11px] border ${result.ok
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                        : 'bg-white border-rose-300 text-rose-900'}`}>
                    {result.ok ? (
                        <>
                            <b>✓ Başarılı:</b> {result.message || 'bpost test shipment + label OK'}
                        </>
                    ) : (
                        <>
                            <b>✗ Hata ({result.stage || '?'}):</b> {result.error}
                            {result.raw_bpost_error && (
                                <div className="mt-1 text-rose-700">
                                    <i>bpost ham hata:</i> {result.raw_bpost_error}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function BpostCard() {
    const cfgQuery = useBpostConfig();
    const cfg = cfgQuery.data;
    const configured = !!cfg?.configured;
    const saveMut = useSaveBpostConfig();
    const deleteMut = useDeleteBpostConfig();

    const [publicKey, setPublicKey] = useState('');
    const [privateKey, setPrivateKey] = useState('');
    const [shopUrl, setShopUrl] = useState('');

    // Suggested shop URL'i auto-fill
    useEffect(() => {
        if (!shopUrl && cfg?.suggested_shop_url) setShopUrl(cfg.suggested_shop_url);
    }, [cfg?.suggested_shop_url, shopUrl]);

    const handleSave = async () => {
        if (!publicKey || !privateKey) {
            toast.error('Public Key ve Private Key gerekli');
            return;
        }
        try {
            const r = await saveMut.mutateAsync({ publicKey, privateKey, shopUrl });
            toast.success(`bpost bağlandı — ${r.carrier_count} kurye aktif`);
            setPublicKey(''); setPrivateKey('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('bpost bağlantısını kaldır?')) return;
        try {
            await deleteMut.mutateAsync();
            toast.success('Bağlantı kaldırıldı');
        } catch {
            toast.error('Hata');
        }
    };

    return (
        <ProviderCard
            name="bpost (Plug-in v3)"
            desc="Bol etiket veremediği split kutular için yedek kargo. Plug-in Connect API (Public/Private Key)."
            configured={configured}
            icon={Package}
            accent="rose"
            testId="integration-card-bpost">
            {!configured ? (
                <>
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-[11px] text-rose-900 leading-relaxed">
                        <b>Nereden bulurum:</b><br />
                        1. <a href="https://www.bpost.be/ShmBackEnd/" target="_blank" rel="noreferrer" className="underline font-semibold">bpost Shipping Manager</a> → giriş<br />
                        2. <b>Settings → Plug-ins → Key management</b><br />
                        3. <b>&quot;Create new keys&quot;</b> tıkla → Public + Private key üretilir<br />
                        4. &quot;URL for sending status updates&quot;a aşağıdaki <b>Shop URL</b>&apos;u yapıştır + kaydet<br />
                        5. Public + Private key&apos;i buraya yapıştır
                    </div>
                    <Field label="Public Key (UUID)" type="text" value={publicKey} onChange={setPublicKey}
                        placeholder="070A24AA-A40E-382C-AACF-..."
                        testId="bpost-public-key" />
                    <Field label="Private Key (UUID) — SECRET" type="password" value={privateKey} onChange={setPrivateKey}
                        placeholder="••••••••-••••-••••-••••-••••••••••••"
                        testId="bpost-private-key" />
                    <Field label="Shop URL (callback için bpost'a verilecek)" type="text" value={shopUrl} onChange={setShopUrl}
                        placeholder="https://..."
                        testId="bpost-shop-url" />
                    <div className="text-[10px] text-gray-500 -mt-1">
                        ⓘ Bu URL&apos;i bpost portal&apos;daki &quot;URL for sending status updates&quot; alanına yapıştır.
                        bpost teslimat durumlarını bize buradan gönderecek.
                    </div>
                    <BtnRow>
                        <PrimaryBtn onClick={handleSave} loading={saveMut.isPending} icon={Save} testId="bpost-save">
                            Bağla + Token Al
                        </PrimaryBtn>
                    </BtnRow>
                </>
            ) : (
                <>
                    <div className="bg-gray-50 rounded-md p-2 text-xs space-y-1.5">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-[10px]">Public Key</span>
                            <span className="font-mono text-[10px] text-gray-900">{cfg.public_key}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-[10px]">Private Key</span>
                            <span className="font-mono text-[10px] text-gray-900">{cfg.private_key_masked}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-[10px]">Shop URL</span>
                            <span className="font-mono text-[10px] text-gray-700 truncate ml-2">{cfg.shop_url}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-[10px]">Token expires</span>
                            <span className="text-gray-900">{cfg.token_expires || '—'}</span>
                        </div>
                    </div>
                    {cfg.last_verify_error && (
                        <div className="bg-rose-50 border border-rose-200 rounded-md p-2 text-[11px] text-rose-900">
                            <AlertCircle size={11} className="inline mr-1 -mt-0.5" />
                            Son hata: {cfg.last_verify_error}
                        </div>
                    )}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-[11px] text-emerald-900">
                        <CheckCircle2 size={11} className="inline mr-1 -mt-0.5" />
                        bpost bağlı. Split akışında Bol opsiyon vermediği kutular için otomatik bpost yedek etiketi basılacak.
                    </div>
                    <BpostSenderForm />
                    <BtnRow>
                        <GhostBtn onClick={handleDisconnect} loading={deleteMut.isPending} icon={Trash2} danger testId="bpost-disconnect">
                            Bağlantıyı Kaldır
                        </GhostBtn>
                    </BtnRow>
                </>
            )}
        </ProviderCard>
    );
}

function PickupCard({ cfg, refresh }) {    const p = cfg?.default_pickup || {};
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => { setForm(p || {}); }, [cfg, p]);

    const upd = (k, v) => setForm({ ...form, [k]: v });
    const configured = !!(p.company && p.zipcode);

    const save = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/api/transport/config`, { default_pickup: form }, { headers: hdrs() });
            toast.success('Pickup adresi kaydedildi'); refresh?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Kayit hatasi'); }
        finally { setSaving(false); }
    };

    return (
        <ProviderCard name="Default Pickup Adresi" desc="Cargoboard ve Quicargo siparislerinde varsayilan cikis adresi." configured={configured} icon={MapPin} accent="emerald" testId="integration-card-pickup">
            <div className="grid grid-cols-2 gap-2">
                <Field label="Firma" type="text" value={form.company || ''} onChange={(v) => upd('company', v)} testId="pickup-company" />
                <Field label="Iletisim" type="text" value={form.contact_name || ''} onChange={(v) => upd('contact_name', v)} testId="pickup-contact" />
                <Field label="Sokak" type="text" value={form.street_name || ''} onChange={(v) => upd('street_name', v)} testId="pickup-street" />
                <Field label="No" type="text" value={form.street_number || ''} onChange={(v) => upd('street_number', v)} testId="pickup-no" />
                <Field label="Posta" type="text" value={form.zipcode || ''} onChange={(v) => upd('zipcode', v)} testId="pickup-zip" />
                <Field label="Sehir" type="text" value={form.city || ''} onChange={(v) => upd('city', v)} testId="pickup-city" />
                <Field label="Ulke" type="text" value={form.country || ''} onChange={(v) => upd('country', v)} testId="pickup-country" />
                <Field label="Telefon" type="text" value={form.phone || ''} onChange={(v) => upd('phone', v)} testId="pickup-phone" />
            </div>
            <BtnRow>
                <PrimaryBtn onClick={save} loading={saving} icon={Save} testId="pickup-save">Save</PrimaryBtn>
            </BtnRow>
        </ProviderCard>
    );
}

/* ─── MAIN PAGE ───────────────────────────────────────────────────────────── */

export default function Integrations() {
    const [transportCfg, setTransportCfg] = useState({});
    const [tick, setTick] = useState(0);
    const refresh = useCallback(() => setTick(t => t + 1), []);

    useEffect(() => {
        axios.get(`${API}/api/transport/config`, { headers: hdrs() })
            .then(r => setTransportCfg(r.data || {}))
            .catch(() => {});
    }, [tick]);

    return (
        <DashboardLayout>
            <div className="space-y-5 max-w-6xl mx-auto" data-testid="integrations-root">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        API credentials for external providers. Keys are stored server-side and never sent back to the browser.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <BolCard refresh={refresh} />
                    <ShipsGoCard refresh={refresh} />
                    <CargoboardCard cfg={transportCfg} refresh={refresh} />
                    <QuicargoCard cfg={transportCfg} refresh={refresh} />
                    <BillitCard refresh={refresh} />
                    <TelegramCard refresh={refresh} />
                    <PrintNodeCard />
                    <BpostCard />
                    <div className="md:col-span-2">
                        <PickupCard cfg={transportCfg} refresh={refresh} />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
